// PlatformPanel — Phase 5a platforms endpoint.

import { Card } from '../../primitives';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  SectionHead,
} from '../bits';
import { usePlatforms, type AnalyticsScope } from '../queries';
import {
  fmtNum,
  fmtPct,
  fmtWonShort,
  platformColor,
  platformName,
} from '../../../lib/format';

interface Props {
  scope: AnalyticsScope;
  onSelectPlatform?: (platformId: string) => void;
  selectedPlatformId?: string | null;
}

export function PlatformPanel({ scope, onSelectPlatform, selectedPlatformId }: Props) {
  const q = usePlatforms(scope);

  return (
    <Card style={{ padding: 20 }}>
      <SectionHead title="플랫폼별 매출" sub="기간 내 합계" />
      {q.isLoading && <LoadingState />}
      {q.isError && <ErrorState message={(q.error as Error).message} />}
      {q.data && q.data.length === 0 && <EmptyState />}
      {q.data && q.data.length > 0 && (
        <PlatformBody
          rows={q.data}
          onSelectPlatform={onSelectPlatform}
          selectedPlatformId={selectedPlatformId}
        />
      )}
    </Card>
  );
}

function PlatformBody({
  rows,
  onSelectPlatform,
  selectedPlatformId,
}: {
  rows: {
    platformId: string;
    name?: string;
    revenue: number;
    orders: number;
    share: number;
    storeCount?: number;
    costRate?: number;
  }[];
  onSelectPlatform?: (platformId: string) => void;
  selectedPlatformId?: string | null;
}) {
  const total = rows.reduce((a, b) => a + b.revenue, 0);
  return (
    <>
      <div
        style={{
          display: 'flex',
          height: 10,
          borderRadius: 5,
          overflow: 'hidden',
          marginBottom: 14,
          background: 'var(--bg-muted)',
        }}
      >
        {rows.map((p) => (
          <div
            key={p.platformId}
            title={`${platformName(p.platformId)} ${fmtPct(p.share)}`}
            style={{
              width: `${total ? (p.revenue / total) * 100 : 0}%`,
              background: platformColor(p.platformId),
              transition: 'width 280ms var(--ease-out)',
            }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {rows.map((p) => {
          const active = selectedPlatformId === p.platformId;
          const clickable = !!onSelectPlatform;
          return (
          <div
            key={p.platformId}
            onClick={
              clickable ? () => onSelectPlatform!(p.platformId) : undefined
            }
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr auto auto auto',
              gap: 10,
              alignItems: 'center',
              padding: '8px 10px',
              borderRadius: 6,
              cursor: clickable ? 'pointer' : 'default',
              background: active ? 'var(--primary-bg)' : 'transparent',
              border: active
                ? '1px solid var(--primary-border)'
                : '1px solid transparent',
              transition: 'background 120ms',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: platformColor(p.platformId),
                flexShrink: 0,
              }}
            />
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--fg-1)',
                }}
              >
                {p.name ?? platformName(p.platformId)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                {p.storeCount != null && `${p.storeCount}개 상점 · `}
                점유 {fmtPct(p.share)}
              </div>
            </div>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--fg-1)',
                fontVariantNumeric: 'tabular-nums',
                minWidth: 64,
                textAlign: 'right',
              }}
            >
              {fmtWonShort(p.revenue)}
            </span>
            <span
              style={{
                fontSize: 12,
                color: 'var(--fg-3)',
                fontVariantNumeric: 'tabular-nums',
                minWidth: 56,
                textAlign: 'right',
              }}
            >
              {fmtNum(p.orders)}건
            </span>
            <span
              style={{
                fontSize: 12,
                color: 'var(--fg-2)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {p.costRate != null
                ? `원가 ${p.costRate.toFixed(1)}%`
                : ''}
            </span>
          </div>
          );
        })}
      </div>
    </>
  );
}
